import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Youtube } from "lucide-react";

const SHORTS = [
  { id: "7-_RPbexJFs" },
  { id: "mcOtYHj_PLA" },
  { id: "WVtOuyS6zLA" },
  { id: "vZmz8cIL23Y" },
  { id: "LwybT5WbJJw" },
  { id: "yMlZ7bn-7TA" },
];

const STATS = [
  { k: "80K+", v: "Instagram followers" },
  { k: "50K+", v: "YouTube subscribers" },
  { k: "7.6M+", v: "Total content views" },
  { k: "25–44", v: "Core audience age" },
];

const PILLARS = [
  { t: "Tax & finance literacy", d: "Bite-sized education for individuals and salaried professionals." },
  { t: "Founder & startup", d: "What really happens inside small businesses, told first-hand." },
  { t: "Brand & marketing", d: "Practical insights for entrepreneurs scaling consumer brands." },
];

export default function Collaborations() {
  return (
    <>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 md:pt-24 pb-16 md:pb-24">
        <p className="overline fade-up"><span className="section-mark">For brands</span></p>
        <h1 className="font-display mt-7 text-[44px] sm:text-6xl md:text-7xl font-light tracking-[-0.03em] leading-[0.98] text-ink fade-up fade-up-2">
          A finance-first audience.<br />A creator brands <span className="font-display-italic text-terracotta">trust</span>.
        </h1>
        <p className="mt-8 max-w-2xl text-[15px] sm:text-lg text-ink-soft leading-[1.7] fade-up fade-up-3">
          Across YouTube and Instagram, I create content for an audience that takes
          decisions seriously — founders, working professionals, students of business and
          finance. Authentic recommendations, no fluff.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 fade-up fade-up-4">
          <Link
            to="/contact"
            data-testid="collab-hero-cta"
            className="inline-flex items-center justify-center rounded-full bg-terracotta hover:bg-terracotta-deep text-white px-7 py-4 text-[14px] font-medium tracking-wide transition-colors"
          >
            Submit a Collaboration Brief <ArrowUpRight className="ml-2" size={16} />
          </Link>
          <a
            href="https://www.instagram.com/canitinsharmaa/"
            target="_blank"
            rel="noreferrer"
            data-testid="collab-ig-link"
            className="inline-flex items-center justify-center rounded-full border border-stone-300 px-7 py-4 text-[14px] font-medium tracking-wide hover:border-ink"
          >
            <Instagram size={16} className="mr-2" /> Instagram
          </a>
          <a
            href="https://www.youtube.com/@ca_nitinsharma"
            target="_blank"
            rel="noreferrer"
            data-testid="collab-yt-link"
            className="inline-flex items-center justify-center rounded-full border border-stone-300 px-7 py-4 text-[14px] font-medium tracking-wide hover:border-ink"
          >
            <Youtube size={16} className="mr-2" /> YouTube
          </a>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-[hsl(var(--line))] bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`collab-stat-${i}`}>
              <p className="font-display tabular text-4xl md:text-5xl font-light tracking-[-0.03em] text-ink">{s.k}</p>
              <p className="mt-2 text-[13px] text-ink-soft tracking-wide">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SHORTS GRID */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="overline"><span className="section-mark">Recent shorts</span></p>
            <h2 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] text-ink leading-[1.04]">
              Snackable.<br /><span className="font-display-italic">Memorable</span>.
            </h2>
          </div>
          <a
            href="https://www.youtube.com/@ca_nitinsharma/shorts"
            target="_blank"
            rel="noreferrer"
            data-testid="collab-shorts-link"
            className="inline-flex items-center text-[13px] tracking-wide font-medium text-ink hover:text-terracotta"
          >
            View all on YouTube <ArrowUpRight className="ml-1" size={14} />
          </a>
        </div>

        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {SHORTS.map((v) => (
            <a
              key={v.id}
              data-testid={`collab-short-${v.id}`}
              href={`https://www.youtube.com/shorts/${v.id}`}
              target="_blank"
              rel="noreferrer"
              className="group block card-lift"
            >
              <div className="relative aspect-[9/16] overflow-hidden rounded-2xl shorts-frame ring-1 ring-stone-200">
                <img
                  src={`https://i.ytimg.com/vi/${v.id}/oar2.jpg`}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  onError={(e) => {
                    if (!e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = "1";
                      e.currentTarget.src = `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`;
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="rounded-full bg-white/95 backdrop-blur w-12 h-12 grid place-items-center shadow-lg">
                    <span className="ml-0.5 w-0 h-0 border-y-[7px] border-y-transparent border-l-[11px] border-l-ink" />
                  </span>
                </div>
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase text-ink">
                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta" /> Short
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* CONTENT PILLARS */}
      <section className="bg-white border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="overline"><span className="section-mark">Content pillars</span></p>
            <h2 className="font-display mt-6 text-4xl sm:text-5xl font-light tracking-[-0.03em] text-ink leading-[1.05]">
              Three things,<br /><span className="font-display-italic">done very well</span>.
            </h2>
          </div>
          <ul className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <li
                key={i}
                data-testid={`pillar-${i}`}
                className="rounded-2xl border border-[hsl(var(--line))] p-7 hover:border-ink transition-colors bg-white card-lift"
              >
                <div className="font-display tabular text-3xl font-light text-terracotta">0{i + 1}</div>
                <h3 className="font-display mt-5 text-xl font-light tracking-tight text-ink">{p.t}</h3>
                <p className="mt-3 text-ink-soft text-[14px] leading-relaxed">{p.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="rounded-2xl bg-[#f4ddd2] p-10 md:p-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-2xl">
              <p className="overline"><span className="section-mark">For brand teams</span></p>
              <h2 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] text-ink leading-[1.02]">
                Got a brief?<br />Let's build something <span className="font-display-italic">honest</span>.
              </h2>
              <p className="mt-6 text-ink-soft max-w-lg text-[15px] leading-[1.7]">
                I take on a small number of partner brands each quarter. Tell us about
                the campaign, deliverables and timelines.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="collab-cta"
              className="inline-flex items-center rounded-full bg-ink hover:bg-terracotta text-white px-7 py-4 text-[14px] font-medium tracking-wide transition-colors self-start"
            >
              Submit Collaboration Brief <ArrowUpRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
